import { Link } from 'react-router-dom';
import type { flowIdeas } from '@repletoryreach/sdk-api';
import { Badge, Button, Group, Progress, Stack, Text, Title } from '@repletoryreach/sdk-ui';
import { Alert } from '@repletoryreach/sdk-ui/data-display';
import { Modal } from '@repletoryreach/sdk-ui/overlays';
import { Select } from '@repletoryreach/sdk-ui/inputs';
import { openConfirmModal } from '@repletoryreach/sdk-ui/modals';
import { Check } from '@repletoryreach/sdk-ui/icons';
import { useT } from '../../../i18n/I18nProvider';
import { formatCredits } from '../../../lib/credits';
import { formatCount } from '../../../lib/format';
import { buildProgress, buildTotal, fitsPlan, sentToBuild, toReplace } from './lib/data';

const MUTED = 'var(--rr-color-text-muted)';

/**
 * The bar under the suggestions while there is something to pick: how many are picked, the model that builds them and
 * what that costs, the plan's room, and that every flow is created switched off. Build opens the confirmation; nothing
 * is built without it.
 */
export function BuildBar({ items, picked, options, model, onModel, onBuild, busy }: {
  items: flowIdeas.FlowIdeaItem[];
  picked: ReadonlySet<number>;
  options?: flowIdeas.FlowIdeaOptions;
  model: string;
  onModel: (code: string) => void;
  onBuild: () => void;
  busy: boolean;
}) {
  const t = useT();
  const chosen = options?.models.find((m) => m.code === model);
  const total = buildTotal(items, picked, chosen);
  const room = options?.plan.room;
  const count = picked.size;
  return (
    <div className="rr-ideas__buildbar" data-testid="ideas-buildbar">
      <Stack gap={2} style={{ minWidth: 0 }}>
        <Text role="bodySmall" fw={700} data-testid="ideas-picked-count">{t('flowIdeas.build.picked', { count })}</Text>
        <Text role="caption" c={MUTED} data-testid="ideas-build-total">
          {count === 0 ? t('flowIdeas.build.pickSome')
            : total.minor == null ? t('flowIdeas.build.totalUnknown') : t('flowIdeas.build.total', { credits: formatCredits(total.minor, t) })}
        </Text>
        <Text role="caption" c={MUTED} data-testid="ideas-plan-room">
          {room == null ? t('flowIdeas.build.roomUnlimited') : t('flowIdeas.build.room', { count: room })}
        </Text>
      </Stack>
      <div className="rr-ideas__buildbar-model">
        <Select label={t('flowIdeas.build.model')} value={model} onChange={(e) => onModel(e.currentTarget.value)}
          description={chosen?.build_minor == null ? t('flowIdeas.settings.priceUnknown')
            : t('flowIdeas.settings.models.buildPrice', { credits: formatCredits(chosen.build_minor, t) })}
          data={(options?.models ?? []).map((m) => ({ value: m.code, label: m.is_default ? t('flowIdeas.settings.modelStandard', { name: m.name }) : m.name }))}
          data-testid="ideas-buildbar-model" />
      </div>
      <Button onClick={onBuild} disabled={count === 0 || busy} data-testid="ideas-build">
        {t('flowIdeas.build.button', { count })}
      </Button>
      <Text role="caption" c={MUTED} className="rr-ideas__buildbar-note">{t('flowIdeas.build.switchedOff')}</Text>
    </div>
  );
}

/** The confirmation before anything is built: which flows, the model, the price, the plan's room and that they start off. */
export function BuildConfirm({ opened, onClose, items, picked, options, model, onConfirm, loading, error }: {
  opened: boolean;
  onClose: () => void;
  items: flowIdeas.FlowIdeaItem[];
  picked: ReadonlySet<number>;
  options?: flowIdeas.FlowIdeaOptions;
  model: string;
  onConfirm: () => void;
  loading: boolean;
  error: string | null;
}) {
  const t = useT();
  const chosen = options?.models.find((m) => m.code === model);
  const list = items.filter((i) => picked.has(i.n));
  const total = buildTotal(items, picked, chosen);
  const room = options?.plan.room;
  const fit = fitsPlan(list, room);
  return (
    <Modal opened={opened} onClose={onClose} title={t('flowIdeas.confirm.title', { count: list.length })} size="md">
      <Stack gap="sm" data-testid="ideas-confirm">
        <ol className="rr-ideas__steps">{list.map((i) => <li key={i.n}>{i.proposal.name}{i.proposal.kind === 'improve' ? ` (${t('flowIdeas.card.improve')})` : ''}</li>)}</ol>
        <Text role="bodySmall">
          {t('flowIdeas.confirm.model', { model: chosen?.name ?? model })}{' '}
          {total.minor == null ? t('flowIdeas.build.totalUnknown') : t('flowIdeas.confirm.price', { credits: formatCredits(total.minor, t) })}
        </Text>
        <Text role="bodySmall">{t('flowIdeas.confirm.off')}</Text>
        {list.some((i) => i.proposal.kind === 'improve') && <Text role="bodySmall">{t('flowIdeas.confirm.improve')}</Text>}
        {fit < list.length && (
          <Alert color="warning" data-testid="ideas-confirm-room">{t('flowIdeas.confirm.room', { fit, count: list.length })}</Alert>
        )}
        <Text role="caption" c={MUTED}>{t('flowIdeas.confirm.leave')}</Text>
        {error && <Alert color="error" role="alert">{error}</Alert>}
        <Group justify="flex-end" gap="sm">
          <Button variant="default" onClick={onClose}>{t('action.cancel')}</Button>
          <Button onClick={onConfirm} loading={loading} disabled={list.length === 0} data-testid="ideas-confirm-build">
            {t('flowIdeas.build.button', { count: list.length })}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

const STATUS_COLOR: Record<string, string> = { queued: 'gray', building: 'blue', created: 'green', failed: 'red', skipped: 'orange' };

/**
 * The flows sent to be built: one row each (waiting, building, created, or not created with why), an overall bar, and
 * for each created flow Open in builder with what Build with AI found and what it still needs. A built improvement
 * replaces its old version (2 November 2026): SJM built improved copies, deleted the originals by hand and switched
 * the copies on, and the copies' Run another flow steps still opened the deleted flows. Replace finishes the job (links,
 * saved keys and history move; the copy takes the old name and is switched on; the old version is deleted and can be
 * put back for 30 days), so the box at the top offers it for every improvement still waiting.
 */
export function BuildResults({ set, onReplace, onReplaceAll, onRestore, acting }: {
  set: flowIdeas.FlowIdeaSet;
  onReplace: (n: number) => void;
  onReplaceAll: () => void;
  onRestore: (n: number) => void;
  /** The item a replace or a restore is under way for, or `all`. */
  acting: number | 'all' | null;
}) {
  const t = useT();
  const sent = sentToBuild(set.items);
  if (sent.length === 0) return null;
  const progress = buildProgress(set.items);
  const building = set.status === 'building';
  const pending = building ? [] : toReplace(set.items);
  return (
    <section className="rr-ideas__card" data-testid="ideas-results" aria-label={t('flowIdeas.results.title')}>
      {pending.length > 0 && <ReplaceBox items={pending} onReplaceAll={onReplaceAll} busy={acting !== null} loading={acting === 'all'} />}
      <Group justify="space-between" align="flex-start" gap="sm" wrap="wrap">
        <Stack gap={2}>
          <Title order={4}>{building ? t('flowIdeas.results.building') : t('flowIdeas.results.title')}</Title>
          <Text role="caption" c={MUTED} data-testid="ideas-results-count">
            {t('flowIdeas.results.count', { done: formatCount(progress.done), total: formatCount(progress.total), created: formatCount(progress.created) })}
          </Text>
        </Stack>
      </Group>
      <Progress value={progress.total > 0 ? Math.round((progress.done / progress.total) * 100) : 0} size="sm" striped animated={building}
        aria-label={t('flowIdeas.results.title')} />
      {building && <Text role="caption" c={MUTED}>{t('flowIdeas.results.leave')}</Text>}
      <ol className="rr-ideas__results">
        {sent.map((i) => {
          const state = i.status === 'created' ? 'done' : i.status === 'building' ? 'now' : i.status === 'failed' || i.status === 'skipped' ? 'failed' : 'later';
          return (
            <li key={i.n} className={`rr-ideas__result is-${state}`} data-testid="ideas-result" data-status={i.status} data-n={i.n}>
              <span className="rr-ideas__dot" aria-hidden>{state === 'done' ? <Check size={12} /> : state === 'failed' ? '!' : ''}</span>
              <div className="rr-ideas__result-body">
                <Group gap={6} wrap="wrap">
                  <Text role="bodySmall" fw={600}>{i.flow_name ?? i.proposal.name}</Text>
                  <Badge size="sm" variant="light" color={STATUS_COLOR[i.status] ?? 'gray'}>{t(`flowIdeas.itemStatus.${i.status}`)}</Badge>
                  {i.status === 'created' && !i.switched && !i.replaced_at && <Badge size="sm" variant="outline">{t('flowIdeas.results.off')}</Badge>}
                </Group>
                {i.error && <Text role="caption" c="var(--rr-color-error)">{i.error}</Text>}
                {i.problems.length > 0 && <Detail label={t('flowIdeas.results.problems')} lines={i.problems} />}
                {i.needs.length > 0 && <Detail label={t('flowIdeas.results.needs')} lines={i.needs.map((n) => `${t(`flowIdeas.need.${n.kind}`, n.kind)}: ${n.what}`)} />}
                {i.clashes.length > 0 && (
                  <Detail label={t('flowIdeas.results.clashes')} lines={i.clashes.map((c) => t('flowIdeas.flag.keywordClash', { detail: c.keyword, flow: c.flow_name }))} />
                )}
                {i.status === 'created' && i.flow_id && (
                  <Group gap="xs" wrap="wrap">
                    <Link to={`/flows/${i.flow_id}/builder`}><Button size="xs" variant="default" data-testid="ideas-open-builder">{t('flowIdeas.results.open')}</Button></Link>
                    {i.original_flow_id && <ReplaceActions item={i} onReplace={onReplace} onRestore={onRestore} acting={acting} />}
                  </Group>
                )}
              </div>
            </li>
          );
        })}
      </ol>
    </section>
  );
}

/** What the old version an improvement replaces is called; "the old version" when the suggestion did not say. */
function useNames() {
  const t = useT();
  return (i: flowIdeas.FlowIdeaItem) => ({
    name: i.flow_name ?? i.proposal.name,
    old: i.proposal.improves?.flow_name?.trim() || t('flowIdeas.replace.oldVersion')
  });
}

/**
 * The box at the top of the results while built improvements wait to replace their old versions: how many, what
 * Replace does, and Replace all after a confirmation that lists each pair. When the old version was already deleted
 * by hand (SJM's case), it says so: Replace still finishes the job by moving the links that point at it.
 */
function ReplaceBox({ items, onReplaceAll, busy, loading }: {
  items: flowIdeas.FlowIdeaItem[];
  onReplaceAll: () => void;
  busy: boolean;
  loading: boolean;
}) {
  const t = useT();
  const names = useNames();
  const gone = items.filter((i) => !i.original_exists);
  const confirm = () => openConfirmModal({
    title: items.length === 1 ? t('flowIdeas.replace.oneTitle', { old: names(items[0]).old }) : t('flowIdeas.replace.allTitle', { count: items.length }),
    children: (
      <Stack gap={8} data-testid="ideas-replace-confirm">
        <ul className="rr-ideas__steps">
          {items.map((i) => <li key={i.n}><Text role="bodySmall">{t('flowIdeas.replace.pair', names(i))}</Text></li>)}
        </ul>
        <Text role="bodySmall">{t('flowIdeas.replace.allBody')}</Text>
      </Stack>
    ),
    labels: { confirm: t('flowIdeas.replace.all'), cancel: t('action.back') },
    onConfirm: onReplaceAll
  });
  return (
    <div className="rr-ideas__replace" data-testid="ideas-replace">
      <Text role="bodySmall" fw={700}>
        {items.length === 1 ? t('flowIdeas.replace.titleOne') : t('flowIdeas.replace.title', { count: formatCount(items.length) })}
      </Text>
      <ul className="rr-ideas__steps">
        {(['links', 'keys', 'name', 'old'] as const).map((k) => <li key={k}><Text role="caption">{t(`flowIdeas.replace.what.${k}`)}</Text></li>)}
      </ul>
      {gone.length > 0 && (
        <Text role="caption" c={MUTED} data-testid="ideas-replace-gone">
          {gone.length === 1
            ? t('flowIdeas.replace.goneOne', { old: names(gone[0]).old })
            : t('flowIdeas.replace.gone', { names: gone.map((i) => names(i).old).join(', ') })}
        </Text>
      )}
      <Group gap="sm">
        <Button size="sm" onClick={confirm} loading={loading} disabled={busy && !loading} data-testid="ideas-replace-all">{t('flowIdeas.replace.all')}</Button>
      </Group>
    </div>
  );
}

/** A built improvement's own actions: Replace while it waits, Replaced with Put the old version back after, or why neither. */
function ReplaceActions({ item: i, onReplace, onRestore, acting }: {
  item: flowIdeas.FlowIdeaItem;
  onReplace: (n: number) => void;
  onRestore: (n: number) => void;
  acting: number | 'all' | null;
}) {
  const t = useT();
  const { name, old } = useNames()(i);
  if (!i.replaced_at && i.superseded) {
    return <Text role="caption" c={MUTED} data-testid="ideas-superseded">{t('flowIdeas.replace.superseded')}</Text>;
  }
  if (!i.replaced_at) {
    return (
      <>
        {i.switched && <Badge size="sm" variant="light" color="green">{t('flowIdeas.results.switched')}</Badge>}
        <Button size="xs" variant="light" loading={acting === i.n} disabled={acting !== null && acting !== i.n} data-testid="ideas-replace-one"
          onClick={() => openConfirmModal({
            title: t('flowIdeas.replace.oneTitle', { old }),
            children: <Text role="bodySmall">{t(i.original_exists ? 'flowIdeas.replace.oneBody' : 'flowIdeas.replace.oneBodyGone', { name, old })}</Text>,
            labels: { confirm: t('flowIdeas.replace.one'), cancel: t('action.back') },
            onConfirm: () => onReplace(i.n)
          })}>
          {t('flowIdeas.replace.one')}
        </Button>
      </>
    );
  }
  return (
    <>
      <Badge size="sm" variant="light" color="green" data-testid="ideas-replaced">{t('flowIdeas.replace.replaced')}</Badge>
      {i.restored_flow_id ? (
        <Link to={`/flows/${i.restored_flow_id}/builder`}>
          <Button size="xs" variant="subtle" data-testid="ideas-restored-open">{t('flowIdeas.restore.open')}</Button>
        </Link>
      ) : i.can_restore && (
        <Button size="xs" variant="subtle" loading={acting === i.n} disabled={acting !== null && acting !== i.n} data-testid="ideas-restore"
          onClick={() => openConfirmModal({
            title: t('flowIdeas.restore.title'),
            children: <Text role="bodySmall">{t('flowIdeas.restore.body', { old })}</Text>,
            labels: { confirm: t('flowIdeas.restore.button'), cancel: t('action.back') },
            onConfirm: () => onRestore(i.n)
          })}>
          {t('flowIdeas.restore.button')}
        </Button>
      )}
    </>
  );
}

function Detail({ label, lines }: { label: string; lines: string[] }) {
  return (
    <div>
      <span className="rr-ideas__label">{label}</span>
      <ul className="rr-ideas__steps" style={{ paddingLeft: 18 }}>{lines.map((l, i) => <li key={i}><Text role="caption">{l}</Text></li>)}</ul>
    </div>
  );
}
